import { jsxs, jsx } from "react/jsx-runtime";
import { Paragraph, Box, TextButton } from "@zillow/constellation-10";
import { yieldCallback } from "@zillow/yield-callback";
import React, { useState, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import useValidFieldUpdate from "../../utils/form/useValidFieldUpdate.esm.js";
import { setServerUrl, createMax, fetchRate } from "../../utils/mortgagehelpers/mortgage-helpers.esm.js";
import { commaSeparateNumber } from "../../utils/zhl-calculator.esm.js";
import LegalPopover from "../CalculatorForm/LegalPopover.esm.js";
import CheckboxField from "../Fields/CheckboxField.esm.js";
import TextField from "../Fields/TextField.esm.js";
import { sendFieldInteractionEvent } from "./RefinanceCalculator.clickstream.esm.js";
import { useLastValidRefinanceValues, FieldConfig, REFINANCE_CALC, TOOLTIP, refinancePopoverCmsInfo } from "./RefinanceCalculator.utils.esm.js";
const RefinanceFields = ({
  className
}) => {
  const [advanced, setAdvanced] = useState(false);
  const form = useFormContext();
  const lastValidRefinaneValues = useLastValidRefinanceValues();
  const fieldInterestRate = form.register(FieldConfig.rate.name, FieldConfig.rate.config);
  useEffect(() => {
    setServerUrl();
    createMax();
    if (fieldInterestRate) {
      const DOWN_PAYMENT_PERCENTAGE = 0.2;
      const homePrice = lastValidRefinaneValues.fields.newLoanAmount / (1 - DOWN_PAYMENT_PERCENTAGE);
      const getLatestRate = async () => {
        const rate = await fetchRate({
          homePrice,
          downPayment: Math.round(DOWN_PAYMENT_PERCENTAGE)
        });
        form.setValue(fieldInterestRate.name, rate);
      };
      getLatestRate();
    }
  }, []);
  useSyncInterestRate();
  return /* @__PURE__ */ jsxs("div", { className, children: [
    /* @__PURE__ */ jsx(TextField, { label: "Current loan amount", leftAdornment: "$", name: FieldConfig.loanAmount.name, required: true, config: FieldConfig.loanAmount.config, autoComplete: "off", format: commaSeparateNumber, tooltip: {
      title: "Current loan amount",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "Enter the remaining balance of your current loan." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: "Current loan amount"
      }
    }, onChange: () => {
      sendFieldInteractionEvent("Current loan amount", "form_field");
    } }),
    /* @__PURE__ */ jsx(TextField, { label: "Interest rate", rightAdornment: "%", name: FieldConfig.rate.name, required: true, config: FieldConfig.rate.config, autoComplete: "off", tooltip: {
      title: "Interest rate",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "The default interest rate is based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. You may enter the current interest rate on your home loan in this field." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: "Interest rate"
      }
    }, onChange: () => {
      sendFieldInteractionEvent("Interest rate", "form_field");
    } }),
    /* @__PURE__ */ jsx(TextField, { label: "Current term", name: FieldConfig.term.name, rightAdornment: "months", required: true, config: FieldConfig.term.config, autoComplete: "off", tooltip: {
      title: "Current term",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "Your current term is the number of months originally set by your lender to pay off your existing mortgage (e.g., 30 years, 20 years, 15 years, etc.). To convert your current term from years to months, multiply the number of years by 12. For instance, a 30-year mortgage term is equal to 360 months." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: "Current term"
      }
    }, onChange: () => {
      sendFieldInteractionEvent("Current term", "form_field");
    } }),
    /* @__PURE__ */ jsx(TextField, { label: "Origination year", name: FieldConfig.originationYear.name, config: FieldConfig.originationYear.config, autoComplete: "off", required: true, tooltip: {
      title: "Origination year",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "Enter the year you acquired your current mortgage." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: "Origination year"
      }
    }, onChange: () => {
      sendFieldInteractionEvent("Origination year", "form_field");
    } }),
    /* @__PURE__ */ jsx(TextField, { label: "New loan amount", leftAdornment: "$", name: FieldConfig.newLoanAmount.name, config: FieldConfig.newLoanAmount.config, autoComplete: "off", required: true, format: commaSeparateNumber, tooltip: {
      title: "New loan amount",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "The new loan balance can be lower or higher if you'd like to pay down the principal or take cash out." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: "New loan amount"
      }
    }, onChange: () => {
      sendFieldInteractionEvent("New loan amount", "form_field");
    } }),
    /* @__PURE__ */ jsx(TextField, { label: "New interest rate", rightAdornment: "%", name: FieldConfig.newRate.name, config: FieldConfig.newRate.config, autoComplete: "off", required: true, tooltip: {
      title: "New interest rate",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "The default interest rate is based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. You may enter a different refi interest rate in this field." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: "New interest rate"
      }
    }, onChange: () => {
      sendFieldInteractionEvent("New interest rate", "form_field");
    } }),
    /* @__PURE__ */ jsx(TextField, { label: "New term", name: FieldConfig.newTerm.name, rightAdornment: "months", config: FieldConfig.newTerm.config, autoComplete: "off", required: true, format: commaSeparateNumber, tooltip: {
      title: "New term",
      body: /* @__PURE__ */ jsx(Paragraph, { children: "Your new term is the number of months set for you to pay off your new loan, which replaces your existing mortgage during refinancing. The new term may or may not be the same as your current term. To convert your new term from years to months, multiply the number of years by 12. For instance, a 30-year mortgage term is equal to 360 months." }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: "New term"
      }
    }, onChange: () => {
      sendFieldInteractionEvent("New term", "form_field");
    } }),
    /* @__PURE__ */ jsx(TextField, { label: "Refinance fees", leftAdornment: "$", name: FieldConfig.fees.name, config: FieldConfig.fees.config, autoComplete: "off", required: true, format: commaSeparateNumber, tooltip: {
      title: "Refinance fees",
      body: /* @__PURE__ */ jsx(Paragraph, { children: 'Like all home loans, a refinance loan requires closing costs to pay for things like the loan origination fee, title and appraisal. You can roll the fees into the new loan under "Advanced."' }),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: "Refinance fees"
      }
    }, onChange: () => {
      sendFieldInteractionEvent("Refinance fees", "form_field");
    } }),
    /* @__PURE__ */ jsx(Box, { css: {
      marginBottom: "tighter"
    }, children: /* @__PURE__ */ jsx(TextButton, { type: "button", onClick: yieldCallback(() => {
      setAdvanced(!advanced);
      if (advanced) {
        sendFieldInteractionEvent("Simple", "toggle");
      } else {
        sendFieldInteractionEvent("Advanced", "toggle");
      }
    }), children: advanced ? "Simple" : "Advanced" }) }),
    advanced && /* @__PURE__ */ jsxs(React.Fragment, { children: [
      /* @__PURE__ */ jsx(TextField, { label: "Cash out", leftAdornment: "$", name: FieldConfig.cashOut.name, config: FieldConfig.cashOut.config, autoComplete: "off", required: true, format: commaSeparateNumber, tooltip: {
        title: "Cash out",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "If you have enough equity in your home, you may be able to take cash out during a refinance." }),
        interactionEventCmsInfo: {
          block_type_cd: TOOLTIP,
          block_id: REFINANCE_CALC,
          block_displayed_txt: "Cash out"
        }
      }, onChange: () => {
        sendFieldInteractionEvent("Cash out", "form_field");
      } }),
      /* @__PURE__ */ jsx(CheckboxField, { label: "Roll fees into new loan", name: FieldConfig.rollFees.name, tooltip: {
        title: "Roll fees into new loan",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "If you want to avoid any upfront costs of refinancing, you can include those costs as part of your new loan amount to be paid as part of your future mortgage payments. See how those costs will affect your savings." }),
        interactionEventCmsInfo: {
          block_type_cd: TOOLTIP,
          block_id: REFINANCE_CALC,
          block_displayed_txt: "Roll fees into new loan"
        }
      }, onChange: () => {
        sendFieldInteractionEvent("Roll fees into new loan", "toggle");
      } })
    ] }),
    /* @__PURE__ */ jsx(LegalPopover, { triggerText: "Calculator disclaimer", text: "The refinance calculator is intended to be used for educational purposes only. Savings are not guaranteed; savings are dependent upon your current loan terms, credit profile, and available lender loan terms. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and does not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend.", interactionEventCmsInfo: refinancePopoverCmsInfo })
  ] });
};
function useSyncInterestRate() {
  const form = useFormContext();
  const lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  useValidFieldUpdate(form, async (name, value) => {
    lastValidFields.current = {
      ...lastValidFields.current,
      [name]: value
    };
    if (name === FieldConfig.newLoanAmount.name) {
      const DOWN_PAYMENT_PERCENTAGE = 0.2;
      const newLoanAmount = lastValidFields.current.newLoanAmount;
      const homePrice = newLoanAmount / (1 - DOWN_PAYMENT_PERCENTAGE);
      const nextInterestRate = await fetchRate({
        homePrice,
        downPayment: Math.round(homePrice * DOWN_PAYMENT_PERCENTAGE)
      });
      if (!isSameFloat(nextInterestRate, lastValidFields.current.rate)) {
        form.setValue(FieldConfig.newRate.name, nextInterestRate, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
        form.setValue(FieldConfig.rate.name, nextInterestRate + 0.75, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    }
  });
}
function isSameFloat(float1, float2) {
  return Math.abs(Number(float1.toFixed(2)) - Number(float2.toFixed(2))) < Number.EPSILON;
}
export {
  RefinanceFields as default
};
